import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { onSessionExpired } from "@/lib/sessionExpiredEvent";

/**
 * Global, friendly "session expired" prompt.
 *
 * Mounted once at the app root. Shows when a fetch wrapper dispatches the
 * session-expired event (an unrecoverable 401), instead of surfacing a raw
 * 401 error toast. The user re-authenticates via signOut() → /login.
 */
export default function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { signOut } = useAuthContext();
  const location = useLocation();

  useEffect(() => {
    return onSessionExpired(() => {
      // Ignore if we're already on the login screen (e.g. a background call).
      if (location.pathname.startsWith("/login")) return;
      setOpen(true);
    });
  }, [location.pathname]);

  if (!open) return null;

  const handleSignIn = async () => {
    setBusy(true);
    try {
      await signOut(); // clears session state and navigates to /login
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-desc"
    >
      <div className="ui-card-static flex w-full max-w-sm flex-col items-center gap-4 p-6 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-200">
          <i className="ri-time-line text-2xl" aria-hidden />
        </span>
        <div>
          <h2
            id="session-expired-title"
            className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100"
          >
            Your session has expired
          </h2>
          <p
            id="session-expired-desc"
            className="mt-1.5 text-sm text-slate-500 dark:text-slate-400"
          >
            For your security, please sign in again to continue.
          </p>
        </div>
        <button
          type="button"
          className="ui-btn-primary mt-1 w-full"
          onClick={() => void handleSignIn()}
          disabled={busy}
          autoFocus
        >
          {busy ? "Signing in…" : "Sign in again"}
        </button>
      </div>
    </div>
  );
}
